import { memo, useState } from 'react'
import { useSelector } from 'react-redux'
import { EmptyState, EmptyStateAction, GhostRows } from '../common/EmptyState'
import ListSkeleton from '../common/ListSkeleton'
import { selectTradingAccountsLoaded } from '../../selectors/user'
import { useTradingAccountContext } from '../../hooks/useTradingAccountContext'
import OpenTradingAccountDialog from './OpenTradingAccountDialog'

type Props = {
    subject: 'orders' | 'positions' | 'holdings'
    preview: 'orders' | 'positions' | 'holdings'
    /** Overrides the derived copy, for account states the caller knows better. */
    text?: string
}

/**
 * Empty state for trading tabs when no trading account is usable.
 * Until the account list itself has loaded it shows the tab's skeleton, so a
 * fresh session doesn't flash the demat prompt before the real state is known.
 * With no accounts at all it offers the demat account opening flow;
 * otherwise it asks the user to pick an account from the selector.
 */
const NoTradingAccountEmptyState = ({ subject, preview, text }: Props) => {
    const { mode } = useTradingAccountContext()
    const accountsLoaded = useSelector(selectTradingAccountsLoaded)
    const [dialogOpen, setDialogOpen] = useState(false)
    const noAccounts = mode === 'no-accounts'
    const resolvedText =
        text ??
        (noAccounts
            ? `Open a demat account to view your ${subject}.`
            : `Select a trading account to view your ${subject}.`)

    if (!accountsLoaded) {
        return <ListSkeleton variant={preview} />
    }

    return (
        <>
            <EmptyState
                preview={<GhostRows variant={preview} />}
                action={noAccounts ? <EmptyStateAction label="Open demat account" onClick={() => setDialogOpen(true)} /> : undefined}
                text={resolvedText}
            />
            {noAccounts && <OpenTradingAccountDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />}
        </>
    )
}

export default memo(NoTradingAccountEmptyState)
